// View mesa summary and sync with POS

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { addDoc, collection, doc, getDocs, onSnapshot, query, runTransaction, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { usePOSService } from '../../../hooks/usePOSService';
import { useAutoSync } from '../../../hooks/useAutoSync';
import { useNotifications } from '../../../hooks/useNotifications';
import { RefreshCw, Eye, Clock, Users, LogOut, ArrowLeft, Receipt, Home } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import { signOut } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import { toast } from 'react-toastify';
import Logo from '../../../assets/fonts/Solo-logo.png';

// Format currency with thousand separators
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

interface MesaItem {
  name: string;
  price: number;
  quantity: number;
  note?: string;
  paid?: boolean;
  paidByTransactionId?: string | null;
}

interface MesaData {
  numeroMesa: string;
  /** Current bill session; transactions are listed only when they match this id */
  activeSessionId?: string | null;
  items: MesaItem[];
  status?: string;
  createdAt?: any;
  ultimaActualizacion?: any;
  customerCount?: number;
  posTicketId?: string;
  externalTicketId?: string;
  integrationMode?: string;
  activeSource?: string;
  montoTotal?: number;
  montoTotalConImpuestos?: number;
  montoPagado?: number;
  restante?: number;
  tipTotal?: number;
}

interface TransactionDoc {
  id: string;
  mesaId: string;
  sessionId?: string | null;
  numeroMesa?: string;
  posOrderId?: string | null;
  processor?: string;
  status?: string;
  approved?: boolean;
  subtotal?: number;
  tipAmount?: number;
  taxAmount?: number;
  total?: number;
  createdAt?: { toDate: () => Date };
}

const MesaSummaryScreen: React.FC = () => {
  const { id: mesaId } = useParams();
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [mesaData, setMesaData] = useState<MesaData | null>(null);
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [manualTransactions, setManualTransactions] = useState<TransactionDoc[]>([]);
  const [manualTxReload, setManualTxReload] = useState(0);
  const [manualPayOpen, setManualPayOpen] = useState(false);
  const [manualPayAmount, setManualPayAmount] = useState('');
  const [manualPayMethod, setManualPayMethod] = useState<'efectivo' | 'tarjeta' | 'transferencia'>('efectivo');
  const [manualPaySaving, setManualPaySaving] = useState(false);

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Load mesa from restaurant-scoped path; when session-driven, merge active session items/totals
  useEffect(() => {
    if (!mesaId || !restaurant?.id) return;

    const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
    let unsubSession: (() => void) | null = null;

    const unsubscribe = onSnapshot(mesaRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const base: MesaData = {
        numeroMesa: data.numeroMesa || '',
        activeSessionId: typeof data.activeSessionId === 'string' ? data.activeSessionId : null,
        items: data.items || [],
        status: data.status || 'active',
        createdAt: data.createdAt,
        ultimaActualizacion: data.ultimaActualizacion,
        customerCount: data.customerCount || 0,
        posTicketId: data.posTicketId,
        externalTicketId: data.externalTicketId,
        integrationMode: data.integrationMode,
        activeSource: data.activeSource,
        montoTotal: data.montoTotal ?? 0,
        montoTotalConImpuestos: data.montoTotalConImpuestos ?? undefined,
        montoPagado: data.montoPagado ?? 0,
        restante: data.restante ?? 0,
        tipTotal: data.tipTotal ?? 0,
      };

      const activeSessionId = data.activeSessionId;
      if (typeof activeSessionId !== 'string' || !activeSessionId) {
        unsubSession?.();
        unsubSession = null;
        setMesaData(base);
        return;
      }

      unsubSession?.();
      const sessionRef = doc(mesaRef, 'sessions', activeSessionId);
      unsubSession = onSnapshot(sessionRef, (sessionSnap) => {
        if (!sessionSnap.exists()) {
          setMesaData(base);
          return;
        }
        const s = sessionSnap.data();
        setMesaData({
          ...base,
          items: s.items ?? base.items,
          montoTotal: s.montoTotal ?? base.montoTotal,
          montoTotalConImpuestos: s.montoTotalConImpuestos ?? base.montoTotalConImpuestos,
          montoPagado: s.montoPagado ?? base.montoPagado,
          restante: s.restante ?? base.restante,
          // tipTotal accumulates on both session and mesa; take the max to avoid under-reporting.
          tipTotal: Math.max(Number(s.tipTotal) || 0, Number(base.tipTotal) || 0),
          status: s.estado === 'cerrada' ? 'cerrada' : (s.estado === 'abierta' ? 'ocupada' : base.status),
        });
      });
    });

    return () => {
      unsubscribe();
      unsubSession?.();
    };
  }, [mesaId, restaurant?.id]);
  
  const isRestaurantManual = restaurant?.integrationMode === 'none';
  const isManual = mesaData?.integrationMode === 'manual' || isRestaurantManual;

  // Mark this mesa's unread notifications as read when the details screen opens.
  const { list: notifList, markAsRead: markNotifsRead } = useNotifications(restaurant?.id);
  useEffect(() => {
    if (!mesaId) return;
    const ids = notifList.filter((n) => !n.read && n.mesaId === mesaId).map((n) => n.id);
    if (ids.length > 0) markNotifsRead(ids);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesaId, notifList]);
  
  // Manual mode: load transactions inline (query without orderBy to avoid composite index; sort in memory)
  useEffect(() => {
    if (!isManual) return;
    if (!mesaId || !restaurant?.id) return;

    const ref = collection(db, 'restaurants', restaurant.id, 'transactions');
    const q = query(ref, where('mesaId', '==', mesaId));
    getDocs(q)
      .then((snap) => {
        const list = snap.docs
          .map((d) => {
            const data = d.data() || {};
            return { id: d.id, mesaId: data.mesaId, ...data } as TransactionDoc;
          })
          .sort((a, b) => {
            const at = a.createdAt?.toDate?.()?.getTime() ?? 0;
            const bt = b.createdAt?.toDate?.()?.getTime() ?? 0;
            return bt - at;
          });
        setManualTransactions(list);
      })
      .catch((err) => {
        console.error('MesaSummaryScreen: load transactions', err);
        setManualTransactions([]);
      });
  }, [isManual, mesaId, restaurant?.id, manualTxReload]);

  // Initialize POS service
  const { posService } = usePOSService(restaurant?.id, restaurant?.posConfig);

  // Auto-sync with POS every 30 seconds
  const { syncNow } = useAutoSync({
    mesaId: mesaId || '',
    posService,
    enabled: !!mesaId && !!posService && !isManual && restaurant?.posConfig?.syncSettings?.autoSync !== false,
    intervalSeconds: 30,
    onSyncStart: () => setIsAutoSyncing(true),
    onSyncComplete: (success) => {
      setIsAutoSyncing(false);
      if (success) {
        setLastSyncTime(new Date());
        // Updates happen silently in background
      }
    }
  });

  const handleSyncNow = () => {
    if (syncNow) {
      syncNow();
    }
  };

  // Total con impuesto: use synced value when present, else sum of items + tax
  const totalAmount = mesaData?.items?.reduce((sum, item) => sum + item.quantity * item.price, 0) || 0;
  const taxRate = restaurant?.settings?.taxRate || 18; // Default 18%
  const taxAmount = totalAmount * (taxRate / 100);
  const totalWithTax = mesaData?.montoTotalConImpuestos ?? (totalAmount + taxAmount);
  const montoPagado = mesaData?.montoPagado ?? 0;
  const restante = isManual ? (mesaData?.restante ?? Math.max(0, totalWithTax - montoPagado)) : Math.max(0, totalWithTax - montoPagado);
  const tipTotal = Number(mesaData?.tipTotal) || 0;

  // Only transactions for the current mesa session (reopen = new sessionId → previous txs hidden)
  const currentSessionTransactions = React.useMemo(() => {
    const sid =
      typeof mesaData?.activeSessionId === 'string' && mesaData.activeSessionId.trim()
        ? mesaData.activeSessionId
        : null;
    if (!sid) return [];
    return manualTransactions.filter((tx) => tx.sessionId === sid);
  }, [manualTransactions, mesaData?.activeSessionId]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleDeactivateMesa = async () => {
    if (!mesaId) return;

    // Confirmation dialog
    const confirmed = window.confirm(
      `¿Desactivar Mesa ${mesaData?.numeroMesa}?\n\nLa mesa quedará archivada y no aparecerá en el panel activo.`
    );

    if (!confirmed) return;

    if (!restaurant?.id) {
      toast.error('No se pudo identificar el restaurante');
      return;
    }
    try {
      const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
      await updateDoc(mesaRef, {
        isActive: false,
        status: 'libre',
        estado: 'cerrada',
        activeSessionId: null,
        ultimaActualizacion: serverTimestamp(),
      });

      toast.success(`Mesa ${mesaData?.numeroMesa} desactivada exitosamente`);
      
      // Navigate back to mesas screen
      navigate('/admin/mesas');
    } catch (error: any) {
      console.error('Error deactivating mesa:', error);
      toast.error(`Error al desactivar mesa: ${error.message}`);
    }
  };

  const handleRegisterManualPayment = async () => {
    if (!mesaId || !restaurant?.id) return;
    const amount = Number(manualPayAmount.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Ingresa un monto válido mayor que cero');
      return;
    }

    setManualPaySaving(true);
    const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
    const txCollectionRef = collection(db, 'restaurants', restaurant.id, 'transactions');

    try {
      const txResult = await runTransaction(db, async (transaction) => {
        const mesaSnap = await transaction.get(mesaRef);
        if (!mesaSnap.exists()) throw new Error('Mesa no encontrada');
        const mesa = mesaSnap.data() as Record<string, any>;

        const activeSessionId = typeof mesa.activeSessionId === 'string' ? mesa.activeSessionId : null;
        let total = Number(mesa.montoTotalConImpuestos ?? mesa.montoTotal ?? 0);
        let pagado = Number(mesa.montoPagado ?? 0);
        let restante = Number(mesa.restante ?? Math.max(0, total - pagado));
        let targetRef: any = mesaRef;
        let sessionData: Record<string, any> | null = null;

        if (activeSessionId) {
          const sessionRef = doc(mesaRef, 'sessions', activeSessionId);
          const sessionSnap = await transaction.get(sessionRef);
          if (sessionSnap.exists()) {
            sessionData = sessionSnap.data() as Record<string, any>;
            total = Number(sessionData.montoTotalConImpuestos ?? sessionData.montoTotal ?? total);
            pagado = Number(sessionData.montoPagado ?? 0);
            restante = Number(sessionData.restante ?? Math.max(0, total - pagado));
            targetRef = sessionRef;
          }
        }

        if (restante <= 0.01) {
          throw new Error('CUENTA_YA_PAGADA');
        }

        const effectivePaid = Math.min(amount, restante);
        const nuevoPagado = pagado + effectivePaid;
        const nuevoRestante = Math.max(0, total - nuevoPagado);
        const fullyPaid = nuevoRestante <= 0.01;
        // Admin always closes the mesa manually — never auto-close on full payment.
        const nuevoEstado: 'abierta' = 'abierta';

        const updatePayload: Record<string, any> = {
          montoPagado: nuevoPagado,
          restante: nuevoRestante,
          estado: nuevoEstado,
        };

        if (sessionData?.items && Array.isArray(sessionData.items) && fullyPaid) {
          updatePayload.items = sessionData.items.map((item: any) => ({
            ...item,
            paid: true,
            paidQuantity: item.quantity ?? 1,
          }));
        }

        transaction.update(targetRef, updatePayload);
        transaction.update(mesaRef, {
          montoPagado: nuevoPagado,
          restante: nuevoRestante,
          estado: nuevoEstado,
          status: 'ocupada', // admin closes manually; never auto-flip to libre
          ultimaActualizacion: serverTimestamp(),
        });

        return { effectivePaid, nuevoPagado, nuevoRestante, sessionIdWhenOpened: activeSessionId };
      });

      await addDoc(txCollectionRef, {
        mesaId,
        numeroMesa: mesaData?.numeroMesa ?? mesaId,
        sessionId: txResult.sessionIdWhenOpened ?? null,
        processor: 'manual',
        paymentMethod: manualPayMethod,
        approved: true,
        subtotal: txResult.effectivePaid,
        total: txResult.effectivePaid,
        tipAmount: 0,
        taxAmount: 0,
        type: 'manual',
        createdAt: serverTimestamp(),
      });

      toast.success('Pago manual registrado correctamente');
      setManualPayOpen(false);
      setManualPayAmount('');
      setManualPayMethod('efectivo');
      setManualTxReload((n) => n + 1);
    } catch (error: any) {
      if (error?.message === 'CUENTA_YA_PAGADA') {
        toast.error('La cuenta ya fue pagada');
      } else {
        toast.error(error?.message || 'No se pudo registrar el pago manual');
      }
    } finally {
      setManualPaySaving(false);
    }
  };

  if (!mesaData) {
    return (
      <div className="min-h-screen w-full flex flex-col px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-zinc-400" />
            <p className="text-zinc-500">Cargando datos de la mesa...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6 animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        {/* Left Side - Logo + YAP Text */}
        <div className="flex items-center">
          <Link to="/admin/mesas" className="flex items-center gap-3 group">
            <img 
              src={Logo} 
              alt="YAP Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-yap-primary transition-colors">
              yap
            </span>
          </Link>
        </div>

        {/* Center - Clock Display */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>

        {/* Right Side - Volver a inicio + Bell + Logout */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
          >
            <Home size={18} />
            <span>Volver a inicio</span>
          </Link>
          <div className="w-px h-6 bg-zinc-700" />
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700"></div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Mesa {mesaData.numeroMesa}</h1>
            {!isManual && (
              <div className="flex items-center gap-4 mt-3 text-sm text-zinc-400">
                <div className="flex items-center gap-2 bg-zinc-900/40 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/5">
                  <Users className="w-4 h-4" />
                  <span>{mesaData.customerCount || 0} clientes</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900/40 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/5">
                  <Clock className="w-4 h-4" />
                  <span>{lastSyncTime ? lastSyncTime.toLocaleTimeString() : 'Nunca'}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/mesas')}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/10 text-white rounded-full hover:bg-zinc-800/70 hover:border-yap-primary/40 transition-all text-sm font-semibold shadow-md hover:shadow-[0_0_16px_-6px_rgba(0,255,132,0.25)]"
            >
              <ArrowLeft size={16} />
              <span>Volver al Panel de Mesas</span>
            </button>
          </div>
        </div>

        {/* Mesa Status */}
        <div className="bg-zinc-900/40 backdrop-blur-xl rounded-2xl border border-white/5 p-5 mb-6 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                mesaData.status === 'active' || mesaData.status === 'ocupada' ? 'bg-yellow-500' : 
                mesaData.status === 'cerrada' ? 'bg-zinc-500' : 'bg-yap-primary'
              }`}></div>
              <span className="font-semibold text-white">Estado: {mesaData.status === 'active' ? 'Activa' : (mesaData.status || 'Activa')}</span>
            </div>
            <div className="text-sm text-zinc-400 font-medium">
              {mesaData.ultimaActualizacion ? 
                `Actualizado: ${
                  mesaData.ultimaActualizacion.seconds 
                    ? new Date(mesaData.ultimaActualizacion.seconds * 1000).toLocaleString()
                    : typeof mesaData.ultimaActualizacion === 'string'
                    ? new Date(mesaData.ultimaActualizacion).toLocaleString()
                    : 'Fecha inválida'
                }` : 
                'Sin actualizaciones'
              }
            </div>
          </div>
          
          {/* POS Ticket ID (hidden in manual mode) */}
          {!isManual && (mesaData.posTicketId || mesaData.externalTicketId) && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-sm text-zinc-400">
                <span className="font-medium">POS Ticket ID:</span>{' '}
                <span className="text-zinc-300 font-mono text-xs">
                  {mesaData.posTicketId || mesaData.externalTicketId}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Items List (hidden in manual mode) */}
        {!isManual && (
          <>
            {mesaData.items.length === 0 ? (
              <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 p-5 text-center transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
                <Eye className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                <p className="text-zinc-300 text-base font-semibold">Esta mesa no tiene ítems registrados</p>
                <p className="text-zinc-500 text-xs mt-1.5 font-medium">Los ítems aparecerán aquí cuando se sincronicen desde el POS</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Items */}
                <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 overflow-hidden transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
                  <div className="px-4 py-3 border-b border-white/5 bg-zinc-900/20 flex items-center justify-between">
                    <h3 className="text-base font-bold text-white tracking-tight">Ítems de la Mesa</h3>
                    <Link
                      to={`/admin/mesa/${mesaId}/transactions`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-800/80 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors text-xs font-semibold"
                    >
                      <Receipt className="w-3.5 h-3.5" />
                      Ver transacciones
                    </Link>
                  </div>
                  <div className="divide-y divide-white/5">
                    {mesaData.items.map((item, index) => (
                      <div key={index} className="px-4 py-3 hover:bg-zinc-800/40 transition-colors">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-white text-sm">{item.name}</h4>
                            {item.note && (
                              <p className="text-xs text-zinc-400 mt-1 font-medium">Nota: {item.note}</p>
                            )}
                            {item.paid && (
                              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-900/40 text-green-400 border border-green-500/30">
                                  Pagado
                                </span>
                                {item.paidByTransactionId && (
                                  <Link
                                    to={`/admin/mesa/${mesaId}/transactions`}
                                    className="text-[10px] text-yap-primary hover:underline font-medium"
                                  >
                                    Transacción
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xs text-zinc-400 font-medium">
                              {item.quantity} × RD${formatCurrency(item.price)}
                            </div>
                            <div className="font-semibold text-yap-primary text-sm mt-0.5">
                              RD${formatCurrency(item.quantity * item.price)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary - compact, premium */}
                <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 p-4 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
                  <h3 className="text-base font-bold text-white mb-3 tracking-tight">Resumen de cuenta</h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-zinc-400">
                      <span className="font-medium">Subtotal</span>
                      <span className="font-semibold text-zinc-300">RD${formatCurrency(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-zinc-400">
                      <span className="font-medium">Impuesto ({taxRate}%)</span>
                      <span className="font-semibold text-zinc-300">RD${formatCurrency(taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-zinc-300 pt-1.5 border-t border-white/5">
                      <span className="font-semibold">Total con impuesto</span>
                      <span className="font-bold text-yap-primary">RD${formatCurrency(totalWithTax)}</span>
                    </div>
                    <div className="flex justify-between text-green-400 pt-1.5 border-t border-white/5">
                      <span className="font-medium">Pagado</span>
                      <span className="font-semibold">RD${formatCurrency(montoPagado)}</span>
                    </div>
                    {tipTotal > 0 && (
                      <div className="flex justify-between text-amber-400">
                        <span className="font-medium">Propinas</span>
                        <span className="font-semibold">RD${formatCurrency(tipTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-red-400">
                      <span className="font-medium">Restante</span>
                      <span className="font-semibold">RD${formatCurrency(restante)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Manual mode: totals + payment state only */}
        {isManual && (
          <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 p-5 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
            <h3 className="text-base font-bold text-white mb-3 tracking-tight">Resumen de cuenta</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-400">
                <span className="font-medium">Total</span>
                <span className="font-semibold text-zinc-300">RD${formatCurrency(totalWithTax)}</span>
              </div>
              <div className="flex justify-between text-green-400 pt-1.5 border-t border-white/5">
                <span className="font-medium">Pagado</span>
                <span className="font-semibold">RD${formatCurrency(montoPagado)}</span>
              </div>
              {tipTotal > 0 && (
                <div className="flex justify-between text-amber-400">
                  <span className="font-medium">Propinas</span>
                  <span className="font-semibold">RD${formatCurrency(tipTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-red-400 pt-1.5">
                <span className="font-medium">Restante</span>
                <span className="font-semibold">RD${formatCurrency(restante)}</span>
              </div>
            </div>

            {/* Transactions (manual mode only) */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="mb-4">
                <button
                  type="button"
                  disabled={manualPaySaving || restante <= 0.01}
                  onClick={() => setManualPayOpen(true)}
                  className="w-full px-4 py-2 rounded-lg bg-yap-primary text-black font-semibold disabled:opacity-50"
                >
                  Registrar pago manual
                </button>
              </div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-white tracking-tight">Transacciones</h4>
                <Link
                  to={`/admin/mesa/${mesaId}/transactions`}
                  className="text-xs font-semibold text-yap-primary hover:underline"
                >
                  Ver todas
                </Link>
              </div>

              {currentSessionTransactions.length === 0 ? (
                <p className="text-sm text-zinc-400">No hay transacciones para esta mesa.</p>
              ) : (
                <div className="space-y-3">
                  {currentSessionTransactions.slice(0, 5).map((tx) => {
                    const txTotal = typeof tx.total === 'number' ? tx.total : (tx.subtotal ?? 0);
                    const txTip = typeof tx.tipAmount === 'number' ? tx.tipAmount : 0;
                    const billPaid = txTotal > 0 ? Math.max(0, txTotal - txTip) : 0;

                    const created =
                      tx.createdAt && typeof tx.createdAt.toDate === 'function'
                        ? tx.createdAt.toDate()
                        : null;

                    return (
                      <div
                        key={tx.id}
                        className="bg-zinc-900/40 rounded-lg border border-white/10 p-3 flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs text-zinc-400 font-medium">
                            {created
                              ? created.toLocaleDateString('es-DO', {
                                  day: '2-digit',
                                  month: 'short',
                                })
                              : '—'}{' '}
                            {created
                              ? created.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })
                              : ''}
                          </div>
                          <div className="text-xs text-zinc-500 font-medium">{tx.processor || 'Stripe'}</div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-400 font-medium">Pago</span>
                          <span className="text-sm font-bold text-green-400">RD${formatCurrency(billPaid)}</span>
                        </div>

                        {txTip > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-400 font-medium">Propina extra</span>
                            <span className="text-sm font-bold text-emerald-300">RD${formatCurrency(txTip)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end items-center mt-8">
          <button
            onClick={handleDeactivateMesa}
            className="flex items-center gap-2 px-6 py-3 bg-red-900/40 backdrop-blur-xl border border-red-500/30 text-red-400 rounded-full hover:bg-red-800/60 hover:border-red-500/50 hover:text-red-300 transition-all font-semibold shadow-lg hover:shadow-[0_0_20px_-5px_rgba(239,68,68,0.3)]"
          >
            Desactivar Mesa
          </button>
        </div>
      </div>

      {manualPayOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-4">Registrar pago manual</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Monto</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={manualPayAmount}
                  onChange={(e) => setManualPayAmount(e.target.value)}
                  placeholder="Ej: 500.00"
                  className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-300 mb-2">Método</label>
                <select
                  value={manualPayMethod}
                  onChange={(e) => setManualPayMethod(e.target.value as 'efectivo' | 'tarjeta' | 'transferencia')}
                  className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  disabled={manualPaySaving}
                  onClick={() => setManualPayOpen(false)}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-white rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={manualPaySaving}
                  onClick={handleRegisterManualPayment}
                  className="flex-1 px-4 py-3 bg-yap-primary text-black rounded-lg font-semibold disabled:opacity-50"
                >
                  {manualPaySaving ? 'Guardando...' : 'Guardar pago'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesaSummaryScreen;
